<template>
  <bm-number-q
    v-bind="$attrs"
    :value="innerValue"
    :max-decimal-places="maxDecimalPlaces"
    :min-decimal-places="minDecimalPlaces"
    :integral="integral"
    :min="min"
    :max="max"
    :enforce-min-max="enforceMinMax"
    :read-only-value="readOnlyValue"
    :default-value="null"
    :rules="rulesWithRequired"
    @update:value="onInput"
    @blur="emit('blur')"
  >
    <template #append-outer>
      <slot name="append-outer" />
    </template>
    <template #append>
      <slot name="append" />
    </template>
    <template #prepend-inner>
      <slot name="prepend-inner" />
    </template>
    <template #prepend>
      <slot name="prepend" />
    </template>
  </bm-number-q>
</template>

<script setup lang="ts">
/**
 * Non-nullable number control. Wraps BmNumberQ and only emits real numbers.
 *
 * Keeps an internal number | null for BmNumberQ so the field can be cleared
 * (empty + required error) without emitting null to the parent. On blur,
 * BmNumberQ resets from that inner value — not the last parent number — so
 * an emptied field stays empty/invalid instead of snapping back.
 *
 * Use BmNumberQ when null is a valid emitted value.
 */
import { computed, ref, watch } from "vue";
import BmNumberQ from "@/components/controls/input/BmNumberQ.vue";
import type { ValidationRule } from "@/plugins/validation";
import { ruleSet } from "@/plugins/validation";

interface BmNumberProps {
  value: number;
  /* Emit values rounded to the specified decimal places. The rounded value will be updated visually on blur */
  maxDecimalPlaces?: number | null;
  /* Whether to display a minimum number of decimal places (e.g. render 2 as 2.00 and 4.5 as 4.50)  */
  minDecimalPlaces?: number;
  integral?: boolean;
  /* Prevent the inbuilt browser stepper from dropping below this value */
  min?: number;
  /* Prevent the inbuilt browser stepper from going above this value. */
  max?: number;
  /* Force the number control to only emit values in the min/max range. Not recommended - instead use validation. */
  enforceMinMax?: boolean;
  readOnlyValue?: number | null;
  rules?: readonly ValidationRule[];
}

interface BmNumberEvents {
  (event: "update:value", value: number): void;
  (event: "blur"): void;
}

const props = withDefaults(defineProps<BmNumberProps>(), {
  integral: false,
  min: Number.NEGATIVE_INFINITY,
  max: Number.POSITIVE_INFINITY,
  enforceMinMax: false,
  readOnlyValue: null,
  rules: () => [],
});

const emit = defineEmits<BmNumberEvents>();

const innerValue = ref<number | null>(props.value);

watch(
  () => props.value,
  (val) => {
    innerValue.value = val;
  }
);

const rulesWithRequired = computed(() => [ruleSet.required(), ...props.rules]);

const onInput = (newVal: number | null) => {
  innerValue.value = newVal;
  if (newVal !== null && Number.isFinite(newVal)) {
    emit("update:value", newVal);
  }
};
</script>
